<template>
  <b-collapse
    animation=""
    :open="false"
    :class="['answer card', { 'answer--error': hasError }]"
    @open="openHandler"
  >
    <template #trigger="{ open }">
      <div role="button" class="card-header">
        <a class="card-header-icon answer__drag-handle">
          <b-icon icon="drag" type="is-dark" />
        </a>
        <p class="card-header-title">Answer {{ index + 1 }}</p>
        <div v-if="!open" class="answer__message-trailer has-text-grey">
          {{ message }}
        </div>
        <b-tooltip
          v-if="hasError"
          :label="errors[0]"
          multilined
          position="is-left"
          type="is-danger"
        >
          <a class="card-header-icon">
            <b-icon icon="alert-circle-outline" type="is-danger" />
          </a>
        </b-tooltip>
        <a class="card-header-icon" @click.stop="onDelete(index)">
          <b-icon icon="delete-outline" type="is-dark" />
        </a>
        <a class="card-header-icon">
          <b-icon :icon="open ? 'chevron-up' : 'chevron-down'" />
        </a>
      </div>
    </template>
    <div class="answer__content card-content">
      <div class="answer__group">
        <b-switch
          name="hasMessage"
          :value="hasMessage"
          @input="
            fieldChangeHandler({
              target: { name: 'hasMessage', value: $event }
            })
          "
        >
          <b>Message</b>
        </b-switch>
        <b-field
          v-if="hasMessage"
          class="answer__group-item"
          label="Content Type"
        >
          <v-select
            :key="contentType"
            :clearable="false"
            :options="contentTypes"
            placeholder="Content Type"
            :reduce="option => option.value"
            :value="contentType"
            @input="
              fieldChangeHandler({
                target: { name: 'contentType', value: $event }
              })
            "
          />
        </b-field>
        <mc-markdown-editor
          v-if="hasMessage"
          ref="markdown"
          container-class="answer__mde"
          :value="message"
          @input="
            fieldChangeHandler({
              target: { name: 'message', value: $event }
            })
          "
        />
      </div>
      <div v-if="hasMessage" class="answer__group">
        <b-switch
          name="hasDisplayMessage"
          :value="hasDisplayMessage"
          @input="
            fieldChangeHandler({
              target: { name: 'hasDisplayMessage', value: $event }
            })
          "
        >
          <b>Display Message</b>
        </b-switch>
        <mc-markdown-editor
          v-if="hasDisplayMessage"
          ref="markdown"
          container-class="answer__mde"
          :value="displayMessage"
          @input="
            fieldChangeHandler({
              target: { name: 'displayMessage', value: $event }
            })
          "
        />
      </div>
      <div class="answer__group">
        <b-switch
          name="hasScore"
          :value="hasScore"
          @input="
            fieldChangeHandler({
              target: { name: 'hasScore', value: $event }
            })
          "
        >
          <b>Score</b>
        </b-switch>
        <b-input
          v-if="hasScore"
          expanded
          name="score"
          placeholder="Score"
          type="number"
          :value="score"
          @input="
            fieldChangeHandler({
              target: { name: 'score', value: parseInt($event, 10) }
            })
          "
        />
      </div>
      <div class="answer__group">
        <b-switch
          name="hasTrackAnalytics"
          :value="hasTrackAnalytics"
          @input="
            fieldChangeHandler({
              target: { name: 'hasTrackAnalytics', value: $event }
            })
          "
        >
          <b>Track Analytics</b>
        </b-switch>
        <b-input
          v-if="hasTrackAnalytics"
          expanded
          name="trackAnalytics"
          placeholder="Tracking ID"
          type="text"
          :value="trackAnalytics"
          @input="
            fieldChangeHandler({
              target: { name: 'trackAnalytics', value: $event }
            })
          "
        />
      </div>
      <b-field :addons="false" class="answer__group" group label="Next Prompt">
        <b-switch
          name="isDifferentFlow"
          :value="isDifferentFlow"
          @input="
            fieldChangeHandler({
              target: { name: 'isDifferentFlow', value: $event }
            })
          "
        >
          Flow
          <span class="content is-small">
            (Select if prompt belongs to a different flow)
          </span>
        </b-switch>
        <b-input
          v-if="isDifferentFlow"
          expanded
          name="flowName"
          placeholder="Flow Name"
          type="text"
          :value="flowName"
          @input.native="fieldChangeHandler"
        />
        <mc-auto-suggest
          :options="promptKeys"
          placeholder="Prompt Key"
          :value="nextKey"
          @input="nextKeyChangeHandler"
        />
      </b-field>
    </div>
  </b-collapse>
</template>

<script>
import regex from '../../utils/regex';

export default {
  props: {
    answer: Object,
    index: Number,
    promptKeys: Array,
    onChange: {
      type: Function,
      // eslint-disable-next-line
      default: (answerData, answerIndex) => {
        /* Do something with it */
      }
    },
    onDelete: {
      type: Function,
      //eslint-disable-next-line
      default: answerIndex => {
        /* Delete the answer from list */
      }
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      contentType: 'default',
      contentTypes: [
        {
          label: 'Default',
          value: 'default'
        },
        {
          label: 'Event',
          value: 'event'
        },
        {
          label: 'Video',
          value: 'video'
        }
      ],
      displayMessage: '',
      errors: [],
      flowName: '',
      hasDisplayMessage: false,
      hasMessage: false,
      hasTrackAnalytics: false,
      hasScore: false,
      isDifferentFlow: false,
      message: '',
      nextKey: '',
      score: 0,
      trackAnalytics: ''
    };
  },
  computed: {
    hasError() {
      return this.errors.length > 0;
    }
  },
  mounted() {
    this.updateAnswerData();
  },
  methods: {
    checkForErrors() {
      const errors = [];
      const { promptKey: key, answerTrackingId: trk, flowName } = regex;
      if (!key.pattern.test(this.nextKey)) errors.push(key.message);
      if (this.hasTrackAnalytics && !trk.pattern.test(this.trackAnalytics)) {
        errors.push(trk.message);
      }
      if (this.isDifferentFlow && !flowName.pattern.test(this.flowName)) {
        errors.push(flowName.message);
      }

      this.errors = errors;
    },
    fieldChangeHandler({ target }) {
      this[target.name] = target.value;
      this.checkForErrors();
      this.triggerOnChange();
    },
    openHandler() {
      if (this.$refs.markdown) this.$refs.markdown.refresh();
    },
    nextKeyChangeHandler(newKey) {
      this.nextKey = newKey;
      this.checkForErrors();
      this.triggerOnChange();
    },
    triggerOnChange() {
      const answerData = {};
      let nextKey = '.' + this.nextKey;
      if (this.isDifferentFlow) nextKey = this.flowName + nextKey;
      answerData.nextKey = nextKey;

      if (this.hasMessage) {
        answerData.message = this.message;

        if (this.contentType && this.contentType !== 'default') {
          answerData.contentType = this.contentType;
        }

        if (this.hasDisplayMessage) {
          answerData.displayMessage = this.displayMessage;
        }
      }

      if (this.hasScore) answerData.score = this.score;

      if (this.hasTrackAnalytics) {
        answerData.trackAnalytics = this.trackAnalytics;
      }

      this.onChange(answerData, this.index);
    },
    updateAnswerData() {
      this.hasMessage = this.answer.hasOwnProperty('message');
      if (this.hasMessage) {
        this.message = this.answer.message;
        this.contentType = this.answer.contentType || 'default';
      }

      this.hasDisplayMessage = this.answer.hasOwnProperty('displayMessage');
      if (this.hasDisplayMessage) {
        this.displayMessage = this.answer.displayMessage;
      }

      this.hasScore = this.answer.hasOwnProperty('score');
      if (this.hasScore) this.score = this.answer.score;

      this.hasTrackAnalytics =
        this.answer.trackAnalytics && this.answer.trackAnalytics !== '';
      if (this.hasTrackAnalytics) {
        this.trackAnalytics = this.answer.trackAnalytics;
      }

      const [flowName, nextKey] = this.answer.nextKey.split('.');
      this.isDifferentFlow = Boolean(flowName);
      this.flowName = flowName || '';
      this.nextKey = nextKey || '';

      this.checkForErrors();
    }
  }
};
</script>

<style lang="scss" scoped>
.answer {
  &--error {
    border: 1px solid #cc0000;
  }

  &__content {
    padding: 1rem;
  }

  &__group {
    display: flex;
    flex-direction: column;
    align-items: stretch;

    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }

    /deep/ .input {
      margin-top: 0.5rem;
    }
  }

  &__group-item {
    margin-top: 1rem;

    /deep/ .label {
      font-weight: normal;
    }
  }

  &__mde {
    margin-top: 0.5rem;
  }

  .card-header {
    padding: 0.5rem;
  }

  .card-header-title,
  .card-header-icon {
    padding: 0;
    margin-right: 0.5rem;
  }

  .card-header-title {
    flex-grow: 0;
  }

  &__message-trailer {
    display: flex;
    flex-grow: 1;
    align-items: center;
  }
}
</style>
